/**
 * Non-secret, environment specific configuration for the frontends
 * (admin, user, landing, customer-website, atlascloud-ui).
 *
 * Each frontend reads APP_ENV at build time and calls getFrontendEnv with it.
 * Nothing in here reads the environment itself, because apps/api imports this
 * package too and must not depend on bundler-injected values.
 */

export const FRONTEND_APP_ENVS = ['dev', 'prod'] as const
export type FrontendAppEnv = (typeof FRONTEND_APP_ENVS)[number]

export interface FrontendEnv {
  readonly APP_ENV: FrontendAppEnv
  /** API base URL, without trailing slash */
  readonly backendUrl: string
  /** Base URL of the user app, with trailing slash */
  readonly appUrl: string
  /** Public base URL of the files bucket, without trailing slash */
  readonly s3FilesBase: string
}

const frontendEnvProd: FrontendEnv = Object.freeze({
  APP_ENV: 'prod',
  backendUrl: 'https://backend.atlascloud.se/.netlify/functions/server',
  appUrl: 'https://app.atlascloud.se/',
  s3FilesBase: 'https://atlascloud-files.s3.eu-north-1.amazonaws.com',
})

/** Used by the dev deployments and local development */
const frontendEnvDev: FrontendEnv = Object.freeze({
  APP_ENV: 'dev',
  backendUrl: 'https://backend-dev.atlascloud.se/.netlify/functions/server',
  // Comment in to run against a local API, don't commit it
  // backendUrl: 'http://localhost:9999/.netlify/functions/server',
  appUrl: 'https://app-dev.atlascloud.se/',
  s3FilesBase: 'https://atlascloud-files-dev.s3.eu-north-1.amazonaws.com',
})

const FRONTEND_ENV_BY_APP_ENV: Record<FrontendAppEnv, FrontendEnv> = {
  dev: frontendEnvDev,
  prod: frontendEnvProd,
}

export function isFrontendAppEnv(value: unknown): value is FrontendAppEnv {
  return FRONTEND_APP_ENVS.includes(value as FrontendAppEnv)
}

/** Throws a clear error when APP_ENV is missing or invalid */
export function parseFrontendAppEnv(value: unknown): FrontendAppEnv {
  if (!isFrontendAppEnv(value)) {
    throw new Error(
      `APP_ENV must be one of ${FRONTEND_APP_ENVS.join(', ')}, got: ${String(value)}`,
    )
  }
  return value
}

export function getFrontendEnv(appEnv: unknown): FrontendEnv {
  return FRONTEND_ENV_BY_APP_ENV[parseFrontendAppEnv(appEnv)]
}
